import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import superOrganizationAdminService from '../services/superOrganizationAdminService';
import superOrganizationService from '../services/superOrganizationService';

export const getSuperOrganization = createAsyncThunk('superOrganizationAdmin/get', async ({ id }) => {
  const data = await superOrganizationAdminService.get(id);

  return data;
});

export const updateSuperOrganization = createAsyncThunk('superOrganizationAdmin/update', async ({ id, body }) => {
  const data = await superOrganizationAdminService.update(id, body);

  return data;
});

export const getSpsConfiguration = createAsyncThunk('superOrganizationAdmin/getSpsConfiguration', async (id) => {
  const data = await superOrganizationAdminService.getSpsConfiguration(id);

  return data;
});

export const getEmailContact = createAsyncThunk('superOrganizationAdmin/getEmailContact', async () => {
  return superOrganizationAdminService.getEmailContact();
});

export const updateSpsConfiguration = createAsyncThunk('superOrganizationAdmin/updateSpsConfiguration', async ({ id, body }) => {
  const data = await superOrganizationAdminService.updateSpsConfiguration(id, body);

  return data;
});

export const updateDefaultRole = createAsyncThunk('superOrganizationAdmin/updateDefaultRole', async ({ id, roleId }) => {
  return superOrganizationService.updateDefaultRole(id, roleId);
});

export const listAllSuperOrganizationsForUser = createAsyncThunk('superOrganizationAdmin/listAllSuperOrganizationsForUser', async () => {
  return superOrganizationAdminService.listAllSuperOrganizationsForUser();
});
export const getDeactivatedSuperOrgsForUser = createAsyncThunk('superOrganizationAdmin/getDeactivatedSuperOrgsForUser', async () => {
  return superOrganizationAdminService.getDeactivatedSuperOrgsForUser();
});
export const getScimSuperOrgLimitReached = createAsyncThunk(
  'superOrganizationAdmin/getScimSuperOrgLimitReached',
  async ({ superOrgId }) => {
    return superOrganizationAdminService.getScimSuperOrgLimitReached(superOrgId);
  }
);

const INITIAL_STATE = {
  data: {},
  spsConfiguration: {},
  admins: {},
  isLoading: false,
  isError: false,
  allSuperOrgsForUser: [],
  deactivatedSuperOrgsForUser: [],
  limitReachedDate: null
};

const superOrganizationAdminSlice = createSlice({
  name: 'superOrganizationAdmin',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSuperOrganization.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(getSuperOrganization.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getSuperOrganization.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(updateSuperOrganization.fulfilled, (state, action) => {
        state.allSuperOrgsForUser = state.allSuperOrgsForUser.map((org) => {
          let updatedOrg = { ...org };

          if (org.id === action.payload.id) {
            updatedOrg = action.payload;
          }

          return updatedOrg;
        });
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(updateSuperOrganization.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(updateSuperOrganization.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(listAllSuperOrganizationsForUser.fulfilled, (state, action) => {
        state.allSuperOrgsForUser = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(listAllSuperOrganizationsForUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(listAllSuperOrganizationsForUser.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(getDeactivatedSuperOrgsForUser.fulfilled, (state, action) => {
        state.deactivatedSuperOrgsForUser = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(getDeactivatedSuperOrgsForUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getDeactivatedSuperOrgsForUser.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(getScimSuperOrgLimitReached.fulfilled, (state, action) => {
        state.limitReachedDate = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(getScimSuperOrgLimitReached.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getScimSuperOrgLimitReached.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(getSpsConfiguration.fulfilled, (state, action) => {
        state.spsConfiguration = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(getSpsConfiguration.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getSpsConfiguration.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(getEmailContact.fulfilled, (state, action) => {
        state.admins = action.payload?.email ? [action.payload] : [];
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(getEmailContact.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getEmailContact.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(updateSpsConfiguration.fulfilled, (state, action) => {
        state.spsConfiguration = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(updateSpsConfiguration.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(updateSpsConfiguration.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(updateDefaultRole.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(updateDefaultRole.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(updateDefaultRole.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase('signOutGlobally', () => ({ ...INITIAL_STATE }));
  }
});

export const selectSuperOrganizationAdmin = createSelector(
  [
    (state) => state.superOrganizationAdmin.data,
    (state) => state.superOrganizationAdmin.spsConfiguration,
    (state) => state.superOrganizationAdmin.isError,
    (state) => state.superOrganizationAdmin.isLoading,
    (state) => state.superOrganizationAdmin.allSuperOrgsForUser,
    (state) => state.superOrganizationAdmin.deactivatedSuperOrgsForUser
  ],
  (data, spsConfiguration, isError, isLoading, allSuperOrgsForUser, deactivatedSuperOrgsForUser) => ({
    data,
    spsConfiguration,
    isError,
    isLoading,
    allSuperOrgsForUser,
    deactivatedSuperOrgsForUser
  })
);

export const selectActiveSuperOrganizationRole = (state) =>
  state?.superOrganizationAdmin?.data?.role || state?.account?.data?.active_organization?.super_organization?.role || null;

export const selectLimitReachedDate = (state) => state?.superOrganizationAdmin?.limitReachedDate;

export default superOrganizationAdminSlice.reducer;
